// Canvas planet background engine (ported from mockups/provider-dashboard-planet).
// Single 2D canvas; sphere body + halo pre-rendered offscreen per resize; no shadowBlur.

const TILT = -0.38;
const SPIN = 0.000045; // rad per ms
const TOP_FRAC = 0.25;
const N = 120; // more segments: the bigger sphere would show facets at 72
const LATS = [-60, -40, -20, 0, 20, 40, 60];
const LON_STEP = 20;

const PALETTES = {
  light: {
    grid: 'rgba(34,44,55,0.075)',
    rim: 'rgba(34,44,55,0.14)',
    halo: 'rgba(34,44,55,0.05)',
    haloEnd: 'rgba(34,44,55,0)',
    bodyStart: 'rgba(255,255,255,0.35)',
    bodyMid: 'rgba(69,85,108,0.04)',
    bodyEnd: 'rgba(34,44,55,0.08)',
    dot: '#45556C',
    ring: '25,118,210',
    star: '34,44,55',
    pillBg: 'rgba(255,255,255,0.96)',
    pillBorder: 'rgba(69,85,108,0.22)',
    pillFg: '#45556C',
  },
  dark: {
    grid: 'rgba(220,229,239,0.09)',
    rim: 'rgba(220,229,239,0.2)',
    halo: 'rgba(220,229,239,0.06)',
    haloEnd: 'rgba(220,229,239,0)',
    bodyStart: 'rgba(255,255,255,0.08)',
    bodyMid: 'rgba(174,196,218,0.04)',
    bodyEnd: 'rgba(220,229,239,0.09)',
    dot: '#dce5ef',
    ring: '100,181,246',
    star: '226,236,246',
    pillBg: 'rgba(34,58,87,0.96)',
    pillBorder: 'rgba(174,196,218,0.35)',
    pillFg: '#dce5ef',
  },
};

function resolvePalette() {
  return document.documentElement.dataset.theme === 'dark' ? PALETTES.dark : PALETTES.light;
}
const FONT = '600 12px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

export function pluralOrders(n) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'заказ';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'заказа';
  return 'заказов';
}

function pillPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, r);
  } else {
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }
}

function getViewportHeight(canvas) {
  const tg = window.Telegram?.WebApp;
  const h = tg?.viewportStableHeight;
  if (Number.isFinite(h) && h > 0) return h;
  return canvas.clientHeight || window.innerHeight;
}

export function initPlanet(canvas, opts = {}) {
  const ctx = canvas.getContext('2d');
  const blockSelector = opts.blockSelector || 'button, a, [role="button"]';
  const root = opts.root || document.body;
  const rmq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  let W = 0, H = 0, dpr = 1, mobile = false, R = 0, cx = 0, cy = 0;
  let sphere = null; // offscreen canvas
  let stars = [];
  let rot = 0, last = 0, raf = 0, frameInterval = 33;
  let pulses = [], nextSpawn = 0, blocks = [];
  let C = resolvePalette();
  let paused = false, hidden = document.hidden, destroyed = false;
  let reduced = !!rmq?.matches;
  let resizeTimer = 0, remeasureTimer = 0;

  function measure() {
    W = canvas.clientWidth || window.innerWidth;
    H = getViewportHeight(canvas);
    mobile = W < 768;
    dpr = Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 2);
    frameInterval = 1000 / (mobile ? 24 : 30);
    // Horizon fit: the limb passes through the bottom-left corner (0, H) and
    // the circle's top touches the right edge at TOP_FRAC*H, so the arc rises
    // from bottom-left and flattens toward the top-right.
    //   center = (W, TOP_FRAC*H + R),  R = (W^2 + a^2) / (2a),  a = (1-TOP_FRAC)*H
    const topFrac = Math.min(0.3, Math.max(0.2, TOP_FRAC));
    const a = (1 - topFrac) * H;
    R = (W * W + a * a) / (2 * a);
    cx = W;
    cy = topFrac * H + R;
    // Tall screens (W < a): the formula puts the center above the bottom
    // edge, so the limb bulges left of x=0 and crosses the left edge well
    // above the corner. Instead put the center level with the bottom edge,
    // which makes (0, H) the leftmost point of the circle. Keep R = a and
    // shift the center right, off-screen: (a, H).
    if (W < a) {
      R = a;
      cx = a;
      cy = H;
    }
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    buildSphere();
    buildStars();
    remeasureBlocks();
  }

  // Starfield in the "sky" only: points inside the planet disc are skipped,
  // so the horizon arc stays clean. Rebuilt per resize (positions are random).
  function buildStars() {
    const count = Math.min(220, Math.round((W * H) / 8500));
    const out = [];
    for (let i = 0; i < count; i++) {
      const x = Math.random() * W;
      const y = Math.random() * H;
      const dx = x - cx, dy = y - cy;
      if (dx * dx + dy * dy < (R + 6) * (R + 6)) continue;
      out.push({
        x, y,
        r: 0.4 + Math.random() * 1.1,
        a: 0.25 + Math.random() * 0.55,
        phase: Math.random() * Math.PI * 2,
        speed: 0.0004 + Math.random() * 0.0012, // twinkle, rad per ms
      });
    }
    stars = out;
  }

  // Pre-draw halo + body + rim into a viewport-sized offscreen canvas
  // (the full sphere bitmap would be huge at this radius).
  function buildSphere() {
    const pad = R * 0.06;
    const off = document.createElement('canvas');
    off.width = Math.round(W * dpr); off.height = Math.round(H * dpr);
    const o = off.getContext('2d');
    o.scale(dpr, dpr);
    const halo = o.createRadialGradient(cx, cy, R * 0.95, cx, cy, R + pad);
    halo.addColorStop(0, C.halo); halo.addColorStop(1, C.haloEnd);
    o.fillStyle = halo; o.beginPath(); o.arc(cx, cy, R + pad, 0, Math.PI * 2); o.fill();
    const body = o.createRadialGradient(cx - R * 0.35, cy - R * 0.4, R * 0.1, cx, cy, R);
    body.addColorStop(0, C.bodyStart);
    body.addColorStop(0.6, C.bodyMid);
    body.addColorStop(1, C.bodyEnd);
    o.fillStyle = body; o.beginPath(); o.arc(cx, cy, R, 0, Math.PI * 2); o.fill();
    o.strokeStyle = C.rim; o.lineWidth = 1.2; o.beginPath(); o.arc(cx, cy, R, 0, Math.PI * 2); o.stroke();
    sphere = { canvas: off };
  }

  function project(lat, lon) {
    const x0 = Math.cos(lat) * Math.sin(lon + rot);
    const y0 = Math.sin(lat);
    const z0 = Math.cos(lat) * Math.cos(lon + rot);
    const y = y0 * Math.cos(TILT) - z0 * Math.sin(TILT);
    const z = y0 * Math.sin(TILT) + z0 * Math.cos(TILT);
    return [cx + x0 * R, cy - y * R, z];
  }

  function strokeCurve(fn) {
    let open = false;
    for (let i = 0; i <= N; i++) {
      const [x, y, z] = fn(i / N);
      if (z > 0) { if (!open) { ctx.moveTo(x, y); open = true; } else ctx.lineTo(x, y); }
      else open = false;
    }
  }

  function remeasureBlocks() {
    const els = root.querySelectorAll(blockSelector);
    const out = [];
    els.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.width && r.height) out.push({ l: r.left - 8, t: r.top - 8, r: r.right + 8, b: r.bottom + 8 });
    });
    blocks = out;
  }

  function freeAt(x, y, w, h) {
    for (const b of blocks) if (x < b.r && x + w > b.l && y < b.b && y + h > b.t) return false;
    return x > 4 && y > 4 && x + w < W - 4 && y + h < H - 4;
  }

  function pulseGeom(p) {
    const [x, y, z] = project(p.lat, p.lon);
    return { x, y, z, lx: x - p.tw / 2, ly: y - 34 };
  }

  function labelHits(g, tw, other) {
    const o = other.g || pulseGeom(other);
    const ox = o.lx, oy = o.ly;
    return g.lx < ox + other.tw + 6 && g.lx + tw + 6 > ox && g.ly < oy + 28 && g.ly + 28 > oy;
  }

  function zoneFree(g, tw) {
    return freeAt(g.x - 14, g.y - 14, 28, 28) && freeAt(g.lx, g.ly, tw, 22);
  }

  // Spawn at a lat/lon that is currently front-facing, on screen and
  // outside forbidden zones. Returns true on success.
  function spawn(now) {
    ctx.font = FONT;
    for (let tries = 0; tries < 40; tries++) {
      const lat = (Math.random() * 120 - 60) * Math.PI / 180;
      const lon = Math.random() * Math.PI * 2;
      const r = Math.random();
      const n = r < 0.6 ? 1 : r < 0.85 ? 2 : r < 0.95 ? 3 : 5;
      const text = `+${n} ${pluralOrders(n)}`;
      const p = { lat, lon, text, tw: ctx.measureText(text).width + 16, born: now,
        life: 2600 + Math.random() * 900, vis: 0, blocked: false };
      const g = pulseGeom(p);
      if (g.z < 0.35 || !zoneFree(g, p.tw)) continue;
      if (pulses.some((o) => !o.blocked && labelHits(g, p.tw, o))) continue;
      pulses.push(p);
      return true;
    }
    return false;
  }

  function draw(now, animate) {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    // Stars first: they sit behind the planet and gently twinkle while
    // animating (static soft dots when reduced-motion/paused).
    if (stars.length) {
      ctx.fillStyle = `rgb(${C.star})`;
      for (const s of stars) {
        const tw = animate ? 0.55 + 0.45 * Math.sin(now * s.speed + s.phase) : 0.8;
        ctx.globalAlpha = s.a * tw;
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2); ctx.fill();
      }
      ctx.globalAlpha = 1;
    }
    if (sphere) {
      ctx.drawImage(sphere.canvas, 0, 0, W, H);
    }
    ctx.strokeStyle = C.grid; ctx.lineWidth = 1;
    ctx.beginPath();
    for (const d of LATS) { const lat = d * Math.PI / 180; strokeCurve((t) => project(lat, t * Math.PI * 2)); }
    for (let d = 0; d < 360; d += LON_STEP) { const lon = d * Math.PI / 180; strokeCurve((t) => project(-Math.PI / 2 + t * Math.PI, lon)); }
    ctx.stroke();
    if (!animate) return;
    if (now >= nextSpawn) {
      nextSpawn = now + (spawn(now) ? 350 + Math.random() * 700 : 150);
    }
    ctx.font = FONT; ctx.textBaseline = 'middle'; ctx.textAlign = 'center';
    const alive = [];
    for (const p of pulses) {
      const age = now - p.born;
      const g = pulseGeom(p);
      // Once a pulse touches a forbidden zone it fades out for good.
      if (!p.blocked && !zoneFree(g, p.tw)) p.blocked = true;
      // Labels drifting into an older live label: the newer one fades out.
      if (!p.blocked && alive.some((o) => !o.blocked && o.vis > 0.02 && labelHits(g, p.tw, o))) p.blocked = true;
      const depth = Math.max(0, Math.min(1, (g.z - 0.08) / 0.27)); // limb/back fade
      const k = age / p.life;
      const life = k < 0.15 ? k / 0.15 : k > 0.75 ? (1 - k) / 0.25 : 1;
      const target = p.blocked ? 0 : depth * Math.max(0, life);
      p.vis += (target - p.vis) * 0.35;
      if (age >= p.life || (p.blocked && p.vis < 0.02)) continue;
      alive.push(p);
      g.vis = p.vis; p.g = g;
      if (p.vis < 0.02 || g.z <= 0) continue;
      ctx.globalAlpha = p.vis;
      ctx.fillStyle = `rgba(${C.ring},0.08)`; ctx.beginPath(); ctx.arc(g.x, g.y, 14, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = `rgba(${C.ring},${0.35 * (1 - k)})`; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.arc(g.x, g.y, 4 + k * 18, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = C.dot; ctx.beginPath(); ctx.arc(g.x, g.y, 3, 0, Math.PI * 2); ctx.fill();
      pillPath(ctx, g.lx, g.ly, p.tw, 22, 11);
      ctx.fillStyle = C.pillBg; ctx.fill(); ctx.strokeStyle = C.pillBorder; ctx.lineWidth = 1; ctx.stroke();
      ctx.fillStyle = C.pillFg; ctx.fillText(p.text, g.x, g.ly + 11);
    }
    pulses = alive;
    ctx.globalAlpha = 1;
  }

  function tick(now) {
    raf = 0;
    if (destroyed || paused || hidden || reduced) return;
    const dt = now - last;
    // 2ms tolerance + carried remainder so 60Hz vsync hits 30/24fps exactly.
    if (dt >= frameInterval - 2) {
      rot += SPIN * Math.min(dt, 100);
      last = dt > frameInterval * 3 ? now : last + frameInterval; // clamp after long pauses
      draw(now, true);
    }
    raf = requestAnimationFrame(tick);
  }

  function start() {
    if (destroyed) return;
    if (reduced) { pulses = []; draw(0, false); return; }
    if (paused || hidden || raf) return;
    last = performance.now();
    raf = requestAnimationFrame(tick);
  }
  function stop() { if (raf) cancelAnimationFrame(raf); raf = 0; }

  const onResize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => { measure(); if (reduced || paused || hidden) draw(0, false); }, 150);
  };
  const onVis = () => { hidden = document.hidden; if (hidden) stop(); else start(); };
  const onMotion = () => { reduced = !!rmq?.matches; stop(); start(); };
  const tg = window.Telegram?.WebApp;

  const onScroll = () => api.remeasure();
  window.addEventListener('resize', onResize);
  root.addEventListener('scroll', onScroll, { capture: true, passive: true });
  document.addEventListener('visibilitychange', onVis);
  if (rmq) { rmq.addEventListener ? rmq.addEventListener('change', onMotion) : rmq.addListener(onMotion); }
  tg?.onEvent?.('viewportChanged', onResize);
  let ro = null;
  if (typeof ResizeObserver !== 'undefined' && opts.observe) {
    ro = new ResizeObserver(() => api.remeasure());
    ro.observe(opts.observe);
  }

  measure();
  draw(0, false);
  start();

  const api = {
    remeasure() {
      if (remeasureTimer) return; // throttle (also covers scroll)
      remeasureTimer = setTimeout(() => { remeasureTimer = 0; remeasureBlocks(); }, 100);
    },
    // Re-resolve colors after a theme switch; running frames pick them up
    // on the next tick, so only force a redraw while static.
    refreshTheme() {
      C = resolvePalette();
      if (sphere) buildSphere();
      if (reduced || paused || hidden) draw(0, false);
    },
    pause() { paused = true; stop(); },
    resume() { paused = false; start(); },
    destroy() {
      destroyed = true; stop();
      clearTimeout(resizeTimer); clearTimeout(remeasureTimer);
      window.removeEventListener('resize', onResize);
      root.removeEventListener('scroll', onScroll, { capture: true });
      document.removeEventListener('visibilitychange', onVis);
      if (rmq) { rmq.removeEventListener ? rmq.removeEventListener('change', onMotion) : rmq.removeListener(onMotion); }
      tg?.offEvent?.('viewportChanged', onResize);
      ro?.disconnect();
      pulses = []; sphere = null; stars = [];
    },
  };
  return api;
}
